import { useCallback, useRef, useState } from 'react';

//How long to wait for the screen to be ready before giving up on starting.
//Matches useTabJoyride's own probe, which this generalises.
const READY_TIMEOUT_MS = 4000;

/**
 * The state machine every page tour runs, leaving the caller only its steps.
 *
 * Ten hooks each carry a copy of this: the same five-key return, the same
 * "advance past a missing target rather than stall on it", the same "a click on
 * the dimmed backdrop is a miss, not a decision to quit". They have already
 * drifted — some check only `status === 'error'`, which is not the signal
 * Joyride sends when a step's element is absent from the DOM; that arrives as
 * `type === 'error:target_not_found'`, and a tour missing the branch stops dead
 * on the first step whose target has not rendered.
 *
 * The existing hooks are deliberately not retrofitted here — several have their
 * own recovery logic worth keeping (useTabJoyride reads the DOM to work out
 * which modal the user opened) and rewriting them is a far larger change than
 * the tours this was added for. New tours start on this.
 *
 * THE READINESS PROBE IS NOT OPTIONAL. Joyride looks for the FIRST step's
 * target exactly once; if it is not there, the tour sits at lifecycle "init"
 * forever and does not even report target_not_found, so the error branch below
 * cannot rescue it. That is unreachable while a tour can only be started by a
 * button on the screen it describes, and certain the moment it can be started
 * from the header on a tab you are not on yet — which is now every tour here.
 * So the run waits for the first step's target, and any tour that needs a
 * stronger condition than "the container exists" passes its own `waitFor`.
 *
 * @param steps        Joyride step list. Rebuilt per render is fine.
 * @param onBeforeStart  Runs before the wait, for a page that must be put in a
 *                       known state first (switch sub-menu, scroll to top).
 * @param waitFor      Override the probe: a CSS selector, or a predicate
 *                     returning whether the screen is ready.
 * @param startDelay   Delay before the first readiness check.
 */
export default function useSimpleTour(steps, { onBeforeStart, waitFor, startDelay = 100 } = {}) {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    //Callers rebuild `steps` every render (they close over live page state), and
    //the callbacks below only ever need the current values. Holding them in refs
    //keeps both handlers referentially stable, which matters because Joyride
    //re-subscribes on a changed callback and because useTourRegistration would
    //otherwise churn the registry slot on every render.
    const stepsRef = useRef(steps);
    stepsRef.current = steps;
    const beforeRef = useRef(onBeforeStart);
    beforeRef.current = onBeforeStart;
    const waitRef = useRef(waitFor);
    waitRef.current = waitFor;

    const handleStartTutorial = useCallback(() => {
        //Reset first: the help menu is reachable mid-tour, and "start the tour"
        //should mean from the beginning rather than resuming wherever it was.
        setRunTour(false);
        setStepIndex(0);
        beforeRef.current?.();

        const isReady = () => {
            const wait = waitRef.current ?? stepsRef.current[0]?.target;
            if (!wait) return true;
            if (typeof wait === 'function') return !!wait();
            return !!document.querySelector(wait);
        };

        const deadline = Date.now() + READY_TIMEOUT_MS;
        const startWhenReady = () => {
            if (isReady()) {
                setStepIndex(0);
                setRunTour(true);
                return;
            }
            if (Date.now() > deadline) {
                //Not starting beats starting into a tour that cannot advance.
                console.warn('Tour: screen never became ready; not starting.');
                return;
            }
            setTimeout(startWhenReady, 100);
        };
        setTimeout(startWhenReady, startDelay);
    }, [startDelay]);

    const handleJoyrideCallback = useCallback((data) => {
        const { status, action, type, index, origin } = data;

        //Missing the tooltip and hitting the backdrop should not end the tour.
        if (origin === 'overlay' && action === 'close') return;

        //A target that never rendered — an empty list has no first row, a
        //permission-hidden button is simply absent — moves on instead of
        //leaving the user on a tooltip anchored to nothing.
        if (type === 'error:target_not_found' || status === 'error') {
            if (index + 1 < stepsRef.current.length) {
                setStepIndex(index + 1);
                return;
            }
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (status === 'finished' || status === 'skipped' || action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            if (index - 1 < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
            setStepIndex(index - 1);
        }
    }, []);

    return { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback };
}
